import axios from "axios";
import TokenStorage from "utils/TokenStorage.js";
import {
  ensureValidAccessToken,
  refreshAccessToken,
} from "utils/TokenRefresh.js";
import Authenticator from "services/Authenticator.js";

const HTTP = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

const HTTP_UN_INTERCEPTED = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

HTTP_UN_INTERCEPTED.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers.Organization = TokenStorage.getOrganization();
  return config;
});

HTTP_UN_INTERCEPTED.interceptors.response.use((response) => response.data);

function applyAuthHeaders(config) {
  const accessToken = TokenStorage.getAccessToken();
  if (accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  config.headers = config.headers || {};
  config.headers.Organization = TokenStorage.getOrganization();
  return config;
}

function endSessionUnlessRefreshedElsewhere(error) {
  if (TokenStorage.hasUsableAccessToken()) {
    return Promise.resolve();
  }
  Authenticator.clearLocalSession();
  return Promise.reject(error);
}

HTTP.interceptors.request.use(
  async (config) => {
    if (TokenStorage.shouldRefreshAccessToken()) {
      try {
        await ensureValidAccessToken();
      } catch (refreshError) {
        if (!TokenStorage.hasUsableAccessToken()) {
          return endSessionUnlessRefreshedElsewhere(refreshError);
        }
      }
    }
    return applyAuthHeaders(config);
  },
  (error) => Promise.reject(error)
);

HTTP.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config;

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/login/refresh") &&
      TokenStorage.getRefreshToken()
    ) {
      originalRequest._retry = true;
      try {
        await refreshAccessToken();
        applyAuthHeaders(originalRequest);
        return HTTP(originalRequest);
      } catch (refreshError) {
        if (TokenStorage.hasUsableAccessToken()) {
          applyAuthHeaders(originalRequest);
          return HTTP(originalRequest);
        }
        return endSessionUnlessRefreshedElsewhere(refreshError);
      }
    }

    if (status === 401 && !TokenStorage.hasUsableAccessToken()) {
      Authenticator.clearLocalSession();
    }

    return Promise.reject(error);
  }
);

export { HTTP, HTTP_UN_INTERCEPTED };
